<script setup>
// NOTICE: KAPTION-INTEGRATION
// sales-pipeline.LANES.1 — swimlane view: one row per assignee/team with a sticky
// gutter (avatar, name, count), stages as columns. Dragging across columns moves
// the stage; dragging across lanes reassigns. Lanes collapse; terminal columns
// keep their collapsible-strip behaviour.

import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import KaptionSalesPipelineDropList from './KaptionSalesPipelineDropList.vue';
import { laneIdOf, wipState } from 'dashboard/helper/kaptionSalesPipeline.js';

const props = defineProps({
  stages: { type: Array, default: () => [] },
  lanes: { type: Array, default: () => [] },
  groupBy: { type: String, required: true },
  itemsByStage: { type: Object, default: () => ({}) },
  totals: { type: Object, default: () => ({}) },
  hasMore: { type: Object, default: () => ({}) },
  collapsedStages: { type: Object, default: () => ({}) },
  collapsedLanes: { type: Object, default: () => ({}) },
  dragOver: { type: Object, default: () => ({}) },
  moveGuard: { type: Function, default: () => true },
  nowSeconds: { type: Number, default: 0 },
  focusedId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'open',
  'drop',
  'loadMore',
  'toggleCollapse',
  'toggleLane',
  'dragStart',
  'dragEnd',
]);

const { t } = useI18n();
const hoverStageId = ref(null);

const isStrip = stage =>
  !!props.collapsedStages[stage.id] && hoverStageId.value !== stage.id;

// Every stage gets at least its minimum; spare width is shared (1fr) so the
// grid fills the board without card text forcing it wider than the screen.
const stageWidth = stage => {
  if (isStrip(stage)) return ['2.5rem', 2.5];
  if (props.collapsedStages[stage.id]) return ['16rem', 16];
  return ['minmax(12rem, 1fr)', 12];
};
const gridStyle = computed(() => {
  const widths = props.stages.map(stageWidth);
  return {
    gridTemplateColumns: ['11rem', ...widths.map(w => w[0])].join(' '),
    minWidth: `${11 + widths.reduce((sum, w) => sum + w[1], 0)}rem`,
  };
});

const cellItems = (stageId, laneId) =>
  (props.itemsByStage[stageId] || []).filter(
    c => laneIdOf(c, props.groupBy) === laneId
  );

const isDropTarget = (stageId, laneId) =>
  props.dragOver.stageId === stageId && props.dragOver.laneId === laneId;

function hoverStage(stage) {
  hoverStageId.value = props.collapsedStages[stage.id] ? stage.id : null;
}
</script>

<template>
  <div
    class="flex-1 min-h-0 overflow-auto"
    data-testid="pipeline-swimlanes"
    @mouseleave="hoverStageId = null"
  >
    <div class="grid w-full" :style="gridStyle">
      <!-- Header row -->
      <div
        class="sticky top-0 left-0 z-30 bg-n-background border-b border-n-weak px-3 py-2 text-xs font-medium text-n-slate-10"
      >
        {{
          groupBy === 'team'
            ? t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.TEAM')
            : t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.ASSIGNEE')
        }}
      </div>
      <div
        v-for="stage in stages"
        :key="`h-${stage.id}`"
        class="min-w-0 sticky top-0 z-20 bg-n-background border-b border-n-weak px-2 py-2"
        :class="collapsedStages[stage.id] ? 'cursor-pointer' : ''"
        :data-stage-id="stage.id"
        data-testid="pipeline-lane-header"
        @mouseenter="hoverStage(stage)"
        @click="collapsedStages[stage.id] && emit('toggleCollapse', stage.id)"
      >
        <div
          class="h-1 rounded-full mb-1.5"
          :style="{ backgroundColor: stage.color }"
        />
        <div
          v-if="isStrip(stage)"
          class="flex flex-col items-center gap-0.5 text-[11px] font-semibold text-n-slate-11"
          :title="stage.name"
        >
          <i
            :class="
              stage.terminal === 'lost'
                ? 'i-lucide-circle-x'
                : 'i-lucide-trophy'
            "
            class="size-3.5"
            :style="{ color: stage.color }"
          />
          {{ totals[stage.id] || 0 }}
        </div>
        <div v-else class="flex items-center justify-between gap-1">
          <span class="text-xs font-semibold truncate text-n-slate-12">
            {{ stage.name }}
          </span>
          <span
            class="text-[11px] px-1.5 rounded-full shrink-0"
            :class="
              wipState(totals[stage.id] || 0, stage.wipLimit) === 'exceeded'
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{ totals[stage.id] || 0
            }}{{ stage.wipLimit ? `/${stage.wipLimit}` : '' }}
          </span>
          <button
            v-if="stage.terminal"
            type="button"
            class="p-0.5 rounded text-n-slate-10 hover:text-n-slate-12"
            @click.stop="emit('toggleCollapse', stage.id)"
          >
            <i
              :class="
                collapsedStages[stage.id]
                  ? 'i-lucide-pin'
                  : 'i-lucide-chevrons-right'
              "
              class="size-3.5 block"
            />
          </button>
        </div>
      </div>

      <!-- Lanes -->
      <template v-for="lane in lanes" :key="`lane-${lane.id}`">
        <div
          class="sticky left-0 z-10 bg-n-background border-b border-r border-n-weak px-3 py-2 flex items-start gap-2 cursor-pointer"
          :data-lane-id="lane.id"
          data-testid="pipeline-lane-gutter"
          @click="emit('toggleLane', lane.id)"
        >
          <i
            :class="
              collapsedLanes[lane.id]
                ? 'i-lucide-chevron-right'
                : 'i-lucide-chevron-down'
            "
            class="size-4 mt-0.5 text-n-slate-10 shrink-0"
          />
          <Avatar
            v-if="!lane.isNone"
            :src="lane.thumbnail"
            :name="lane.name"
            :size="20"
            rounded-full
          />
          <i v-else class="i-lucide-user-x size-5 text-n-slate-10 shrink-0" />
          <div class="min-w-0">
            <p class="m-0 text-sm font-medium truncate text-n-slate-12">
              {{ lane.name }}
            </p>
            <p class="m-0 text-[11px] text-n-slate-10">
              {{
                t('KAPTION_PLATFORM.SALES_PIPELINE.LANES.COUNT', {
                  count: lane.count,
                })
              }}
            </p>
          </div>
        </div>
        <div
          v-for="stage in stages"
          :key="`c-${lane.id}-${stage.id}`"
          class="min-w-0 border-b border-r border-n-weak p-1.5 transition-colors"
          :class="[
            isDropTarget(stage.id, lane.id) && dragOver.exceeds
              ? 'outline-dashed outline-2 -outline-offset-2 outline-n-ruby-9'
              : isDropTarget(stage.id, lane.id)
                ? 'bg-n-teal-2 outline outline-1 -outline-offset-1 outline-n-teal-8'
                : '',
          ]"
          :data-stage-id="stage.id"
          :data-lane-id="lane.id"
          data-testid="pipeline-lane-cell"
          @mouseenter="hoverStage(stage)"
        >
          <div
            v-if="collapsedLanes[lane.id] || isStrip(stage)"
            class="text-center text-[11px] text-n-slate-10 py-1"
          >
            {{ cellItems(stage.id, lane.id).length || '' }}
          </div>
          <KaptionSalesPipelineDropList
            v-else
            :items="cellItems(stage.id, lane.id)"
            :stage="stage"
            :stages="stages"
            :lane-id="lane.id"
            :move-guard="moveGuard"
            :now-seconds="nowSeconds"
            :focused-id="focusedId"
            condensed
            min-height-class="min-h-[2.75rem]"
            @drop="emit('drop', $event)"
            @open="emit('open', $event)"
            @drag-start="emit('dragStart', $event)"
            @drag-end="emit('dragEnd', $event)"
          />
        </div>
      </template>

      <!-- Load more per column (pages are per stage, shared by every lane) -->
      <div class="sticky left-0 bg-n-background" />
      <div v-for="stage in stages" :key="`m-${stage.id}`" class="p-1.5">
        <button
          v-if="hasMore[stage.id] && !isStrip(stage)"
          type="button"
          class="w-full text-xs py-1 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
          @click="emit('loadMore', stage.id)"
        >
          {{ t('KAPTION_PLATFORM.SALES_PIPELINE.LANES.LOAD_MORE') }}
        </button>
      </div>
    </div>
  </div>
</template>
